import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import httpBuildQuery from 'http-build-query';
import {
  Checkbox,
  Drawer,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel
} from '@material-ui/core';
import { useLocation } from 'react-router-dom';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import { Breadcrumb, BackdropCircular } from 'components';
import {
  getStoreOrdersApi,
  getStoreCustomerGroupsApi,
  getPaymentIntegrationsApi,
  getSurveyQuestionConfigsApi
} from 'api';
import { parseSearchParams } from 'helpers/customers';
import CustomTableToolbar from './CustomTableToolbar';
import OrderRowCollapsible from './OrderRowCollapsible';
import FilterDrawer from './FilterDrawer';
import { useStyles } from './styles';


const defaultTableCells = [
  {
    label: 'Order Id',
    sortField: 'increment_id',
    display: true,
  },
  {
    label: 'Purchase Date',
    sortField: 'created_at',
    display: true,
  },
  {
    label: 'Order Amount',
    sortField: 'grand_total',
    display: true,
  },
  {
    label: 'First Name',
    sortField: 'customer_firstname',
    display: true,
  },
  {
    label: 'Last Name',
    sortField: 'customer_lastname',
    display: true,
  },
  {
    label: 'Nextech status',
    display: false,
  },
  {
    label: 'Mod Med status',
    display: false,
  },
  {
    label: 'Email',
    sortField: 'customer_email',
    display: true,
  },
  {
    label: 'Customer Group',
    sortField: 'customer_group_id',
    display: false,
  },
  {
    label: 'Status',
    sortField: 'status',
    display: true,
  },
  {
    label: 'Survey',
    display: false,
  },
  {
    label: 'Posted',
    sortField: 'delivery_status',
    display: true,
  },
  {
    label: 'Risk Score',
    sortField: 'dermpro_fraud_score',
    display: false,
  },
  {
    label: 'Details',
    display: true,
  }
];

interface ILocationStateType {
  orderId: string
}

interface IOrdersListProps {
  pageTitle?: string
  defaultFilter?: IFilterConditions
}

const OrdersList = ({
  pageTitle = 'Orders',
  defaultFilter = {}
}: IOrdersListProps) => {
  const classes = useStyles();
  const history = useHistory();
  const paramsFromUrl: IParamsFromUrl = parseSearchParams(location.search);
  const { state } = useLocation<ILocationStateType>();
  const dispatch = useDispatch();
  const [sortDirection, setSortDirection] = useState(paramsFromUrl?.sortDirection || 'desc');
  const [sortField, setSortField] = useState(paramsFromUrl?.sortField || 'created_at');
  const [selectedIncrementIds, setSelectedIncrementIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(Number(paramsFromUrl.currentPage) || 1);
  const [rowsPerPage, setRowsPerPage] = useState(Number(paramsFromUrl.rowsPerPage) || 10);
  const [openFilter, setOpenFilter] = useState(false);
  const [filterConditions, setFilterConditions] = useState<IFilterConditions>(paramsFromUrl.filterConditions || defaultFilter);
  const [searchConditions, setSearchConditions] = useState(paramsFromUrl.searchConditions || {});
  const [selectedOrderId, setSelectedOrderId] = useState(state?.orderId);
  const [headTableCells, setHeadTableCells] = useState(defaultTableCells);

  const {
    storeOrders,
    ordersTotalCount,
    loadingOrders,
    sendingOrderEmail,
    savingStoreOrderComment,
    loadingStoreOrderInvoicePdf,
    shipOrderLoading,
    updatingStoreOrder,
    isChangingHoldOrdersStatus,
    isChangingPostedOrdersStatus
  } = useSelector((state: IReduxState) => state.store_orders);
  const { customerGroups } = useSelector((state: IReduxState) => state.customers);
  const { isNextechIntegrated, isModMedIntegrated, storeSurveyQuestionConfigs } = useSelector((state: IReduxState) => state.store_configs);
  const isSurveyEnabled = storeSurveyQuestionConfigs.find((config) => config.path === 'surveyquestion/general/enable')?.value === '1';

  const loading = loadingOrders ||
    sendingOrderEmail ||
    savingStoreOrderComment ||
    loadingStoreOrderInvoicePdf ||
    shipOrderLoading ||
    updatingStoreOrder ||
    isChangingHoldOrdersStatus ||
    isChangingPostedOrdersStatus;

  useEffect(() => {
    const storageTableCells = localStorage.getItem('OrderTableCells');
    let selectedCells = defaultTableCells;

    if (storageTableCells) {
      selectedCells = JSON.parse(storageTableCells);

      // this fix case when add new column but it is missing in storage
      if (selectedCells.length !== defaultTableCells.length) {
        selectedCells = defaultTableCells.map((defaultCell) => {
          const storageCell = selectedCells.find((s) => defaultCell.label === s.label);

          return storageCell || defaultCell;
        });
      }
    }

    if (!isSurveyEnabled) {
      selectedCells = selectedCells.filter((cell) => cell.label !== 'Survey');
    }

    setHeadTableCells(selectedCells);
  }, [headTableCells.length, isSurveyEnabled]);

  useEffect(() => {
    if (customerGroups.length === 0) {
      getStoreCustomerGroupsApi({dispatch});
    }

    getSurveyQuestionConfigsApi({dispatch});
    getPaymentIntegrationsApi({dispatch});
  }, []);

  useEffect(() => {
    if (selectedOrderId) {
      setSearchConditions({field: 'increment_id', value: `%${selectedOrderId}%`, condition_type: 'like'});
      setSelectedOrderId('');
    }

    getStoreOrdersApi({
      dispatch,
      sortDirection,
      sortField,
      pageSize: rowsPerPage,
      currentPage,
      filterConditions: Object.values(filterConditions)
        .filter((condition) => condition.value !== ''),
      searchConditions: selectedOrderId ? {field: 'increment_id', value: `%${selectedOrderId}%`, condition_type: 'like'} : searchConditions
    });

    const queryParams = {
      filterConditions,
      searchConditions,
      currentPage,
      sortDirection,
      sortField,
      rowsPerPage,
    };

    const queryString = httpBuildQuery(queryParams);
    history.replace({
      pathname: location.pathname,
      search: queryString,
    });
  }, [
    sortDirection,
    sortField,
    rowsPerPage,
    currentPage,
    filterConditions,
    searchConditions
  ]);

  const handleRequestSort = (event, property) => {
    const isDesc = sortField === property && sortDirection === 'desc';
    setSortDirection(isDesc ? 'asc' : 'desc');
    setSortField(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelectedRows = storeOrders.map((n) => n.increment_id);
      setSelectedIncrementIds(newSelectedRows);
      return;
    }

    setSelectedIncrementIds([]);
  };

  const handleSelectRow = (event, id) => {
    const selectedIndex = selectedIncrementIds.indexOf(id);
    let newSelectedRows = [];

    if (selectedIndex === -1) {
      newSelectedRows = newSelectedRows.concat(selectedIncrementIds, id);
    } else if (selectedIndex === 0) {
      newSelectedRows = newSelectedRows.concat(selectedIncrementIds.slice(1));
    } else if (selectedIndex === selectedIncrementIds.length - 1) {
      newSelectedRows = newSelectedRows.concat(selectedIncrementIds.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelectedRows = newSelectedRows.concat(
        selectedIncrementIds.slice(0, selectedIndex),
        selectedIncrementIds.slice(selectedIndex + 1),
      );
    }

    setSelectedIncrementIds(newSelectedRows);
  };

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  const isSelected = (id) => selectedIncrementIds.indexOf(id) !== -1;

  const onSearch = (searchValue, searchField) => {
    const condition = {
      field: searchField,
      value: searchValue.length ? `%${searchValue}%` : '',
      conditionType: 'like'
    };
    setSearchConditions(searchValue.length ? condition : {});
    setCurrentPage(1);
  };

  return (
    <div className="flex-grow flex-column relative h-full scroll-y pb-5">
      <div className="mb-sm-30">
        <Breadcrumb
          routeSegments={[
            { name: pageTitle}
          ]}
        />
      </div>
      <div className={classes.card}>
        <div className={`p-5 ${classes.toolbarContainer}`}>
          <CustomTableToolbar
            selectedIncrementIds={selectedIncrementIds}
            numSelected={selectedIncrementIds.length}
            onSearch={onSearch}
            setOpenFilter={setOpenFilter}
            filterConditions={filterConditions}
            tableCells={headTableCells}
            setHeadTableCells={setHeadTableCells}
            hiddenFilters={defaultFilter}
            searchConditions={searchConditions}
          />
        </div>
        <div className="table-container">
          <TableContainer>
            <Table aria-label="collapsible table" className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedIncrementIds.length > 0 && selectedIncrementIds.length < storeOrders.length}
                      checked={storeOrders?.length > 0 && selectedIncrementIds?.length === storeOrders?.length}
                      onChange={handleSelectAllClick}
                      inputProps={{ 'aria-label': 'select orders' }}
                    />
                  </TableCell>
                  {headTableCells.map((cell, index) => {
                    if (!cell.display) {
                      return null;
                    }

                    return (
                      <TableCell
                        key={index}
                        className="min-w-80"
                      >
                        {cell.sortField ? (
                          <TableSortLabel
                            active={sortField === cell.sortField}
                            IconComponent={sortDirection === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                            onClick={(event) => handleRequestSort(event, cell.sortField)}
                          >
                            {cell.label}
                          </TableSortLabel>
                        ) : (
                          cell.label
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {!storeOrders.length ?
                  <TableRow>
                    <TableCell colSpan={Object.keys(headTableCells).length + 1} align="center">
                      No matching orders found.
                    </TableCell>
                  </TableRow>
                  :
                  <>
                    {storeOrders.map((order, index) => {
                      const isItemSelected = isSelected(order.increment_id);
                      const labelId = `enhanced-table-checkbox-${index}`;
                      return (
                        <OrderRowCollapsible
                          key={order.increment_id}
                          order={order}
                          isItemSelected={isItemSelected}
                          labelId={labelId}
                          handleSelectRow={handleSelectRow}
                          headTableCells={headTableCells}
                        />
                      );
                    })}
                  </>
                }
              </TableBody>
            </Table>
          </TableContainer>
          {!!ordersTotalCount &&
            <TablePagination
              rowsPerPageOptions={[10, 20, 40, 80, 100]}
              component="div"
              className={classes.paginationContainer}
              count={ordersTotalCount}
              rowsPerPage={rowsPerPage}
              page={currentPage - 1}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          }
        </div>
      </div>
      <Drawer
        anchor="right"
        open={openFilter}
        onClose={() => setOpenFilter((openFilter) => !openFilter)}
        PaperProps={{ style: { maxWidth: '400px' } }}
      >
        <FilterDrawer
          setOpenFilter={setOpenFilter}
          setFilterConditions={setFilterConditions}
          filterConditions={filterConditions}
          isNextechIntegrated={isNextechIntegrated}
          isModMedIntegrated={isModMedIntegrated}
          hiddenFilters={defaultFilter}
        />
      </Drawer>
      <BackdropCircular loading={loading} />
    </div>
  );
};

export default OrdersList;
